import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useFormAutoSave } from '../../hooks/useFormAutoSave';
import type { CustomField } from '../CustomFieldBuilder';
import { ROLES, Volunteer, VolunteerForm } from '../../hooks/useVolunteers';

// Édition d'un bénévole dans un panneau latéral (2026-09-27), comme une équipe :
// la liste reste visible, ↑ ↓ passe au voisin, et TOUT s'édite — identité,
// rôle, disponibilité, chaque réponse. L'ancienne fenêtre n'éditait que le
// nom, l'email et les réponses libres.
//
// Ce que le panneau écrit :
//  - le rôle DIRECTEMENT (il fait autorité sur la réponse au formulaire) ;
//  - la disponibilité par la RÉPONSE à la question du formulaire : ce sont les
//    cases que le bénévole a vues, et le serveur en déduit les créneaux — les
//    éditer en heures brutes ici ferait deux vérités pour une même donnée.
//
// Sauvegarde automatique (règle du 22/09) : identité et réponses libres au
// blur, rôle, cases et listes au choix. Le panneau n'existe qu'en édition (un
// bénévole s'inscrit lui-même), il n'a donc plus aucun bouton d'écriture.

export interface VolunteerFormValues {
  first_name: string;
  last_name: string;
  email: string;
  /** '' = sans rôle. */
  role: string;
  answers: Record<string, any>;
}

export interface VolunteerPatch {
  first_name?: string;
  last_name?: string;
  email?: string;
  role?: string | null;
  answers?: Record<string, any>;
}

const NONE = '__none';

function toValues(v: Volunteer | null): VolunteerFormValues {
  return {
    first_name: v?.first_name ?? '',
    last_name: v?.last_name ?? '',
    email: v?.email ?? '',
    role: v?.role ?? '',
    answers: { ...(v?.answers ?? {}) },
  };
}

const toPatch = (fields: (keyof VolunteerFormValues)[], v: VolunteerFormValues): VolunteerPatch => {
  const p: VolunteerPatch = {};
  if (fields.includes('first_name')) p.first_name = v.first_name.trim();
  if (fields.includes('last_name')) p.last_name = v.last_name.trim();
  if (fields.includes('email')) p.email = v.email.trim();
  if (fields.includes('role')) p.role = v.role || null;
  if (fields.includes('answers')) p.answers = v.answers;
  return p;
};

interface Props {
  open: boolean;
  volunteer: Volunteer | null;
  form: VolunteerForm | null;
  saving: boolean;
  serverError: string;
  /** Écriture partielle, immédiate. Lève sur refus. */
  onPatch: (patch: VolunteerPatch) => Promise<void>;
  onRemove: () => void;
  onClose: () => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function VolunteerSheet({
  open, volunteer, form: definition, saving, serverError,
  onPatch, onRemove, onClose, onPrev, onNext,
}: Props) {
  const { t, i18n } = useTranslation();

  const schema = useMemo(() => z.object({
    first_name: z.string().trim().min(1, t('volunteers.sheet.firstNameRequired')),
    last_name: z.string().trim().min(1, t('volunteers.sheet.lastNameRequired')),
    email: z.string().trim().email(t('teams.form.emailInvalid')),
    role: z.string(),
    answers: z.record(z.any()),
  }), [t]);

  const form = useForm<VolunteerFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toValues(volunteer),
    mode: 'onTouched',
  });
  const autoSave = useFormAutoSave<VolunteerFormValues, VolunteerPatch>(form, {
    enabled: !!volunteer,
    save: onPatch,
    toPatch,
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('volunteers.edit.saveError')),
  });
  const { saveFields } = autoSave;

  useEffect(() => {
    autoSave.reset(toValues(volunteer));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [volunteer?.id, open]);

  const availability = definition?.availabilityField ?? null;
  const freeFields = definition?.freeFields ?? [];

  const registeredAt = volunteer
    ? new Date(volunteer.created_at).toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' })
    : '';

  const setAnswer = (id: string, value: unknown) =>
    form.setValue('answers', { ...form.getValues('answers'), [id]: value }, { shouldDirty: true });
  // Un choix (case, liste) s'écrit au clic ; un texte au blur.
  const chooseAnswer = (id: string, value: unknown) => { setAnswer(id, value); void saveFields('answers'); };
  const blurAnswers = () => { void saveFields('answers'); };

  // Une réponse est éditée AVEC SON TYPE — une case reste une case, une liste
  // reste une liste. Tout rendre en texte libre transformerait un booléen en
  // « true » à la première correction, et l'export le montrerait le jour J.
  const renderAnswer = (field: CustomField, answers: Record<string, any>) => {
    const value = answers[field.id];
    if (field.type === 'checkbox' && field.options?.length) {
      const chosen: string[] = Array.isArray(value) ? value : value ? [String(value)] : [];
      return (
        <div className="flex flex-col gap-2">
          {field.options.map((opt) => (
            <label key={opt} className="flex cursor-pointer items-center gap-2 text-sm">
              <Checkbox
                checked={chosen.includes(opt)}
                onCheckedChange={(c) => chooseAnswer(field.id, c === true ? [...chosen, opt] : chosen.filter((o) => o !== opt))}
              />
              {opt}
            </label>
          ))}
        </div>
      );
    }
    if (field.type === 'checkbox') {
      return (
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <Checkbox checked={!!value} onCheckedChange={(c) => chooseAnswer(field.id, c === true)} />
          {t('volunteers.list.yes')}
        </label>
      );
    }
    if (field.type === 'select' || field.type === 'radio') {
      return (
        <Select value={value ? String(value) : NONE} onValueChange={(v) => { if (v) chooseAnswer(field.id, v === NONE ? '' : v); }}>
          <SelectTrigger><SelectValue placeholder={t('volunteers.edit.noAnswer')} /></SelectTrigger>
          <SelectContent>
            <SelectItem value={NONE}>{t('volunteers.edit.noAnswer')}</SelectItem>
            {(field.options || []).map((opt) => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (field.type === 'textarea') {
      return <Textarea rows={3} value={value ?? ''} onChange={(e) => setAnswer(field.id, e.target.value)} onBlur={blurAnswers} />;
    }
    return <Input value={value ?? ''} onChange={(e) => setAnswer(field.id, e.target.value)} onBlur={blurAnswers} />;
  };

  return (
    <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent
        side="right"
        hideClose
        onInteractOutside={(e) => e.preventDefault()}
        className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[560px]"
      >
        <Form {...form}>
          <form onSubmit={(e) => e.preventDefault()} className="flex h-full min-h-0 flex-col" noValidate>
            <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
              <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                <SheetDescription className="text-xs">
                  {volunteer && t('volunteers.sheet.registeredAt', { date: registeredAt })}
                </SheetDescription>
                <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                  {volunteer ? `${volunteer.first_name} ${volunteer.last_name}` : ''}
                </SheetTitle>
              </div>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={onPrev}>
                <ChevronUp size={16} />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={onNext}>
                <ChevronDown size={16} />
              </Button>
              <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={onClose}>
                <X size={16} />
              </Button>
            </div>

            <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-6 py-6">
              <section className="space-y-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('volunteers.sheet.sectionIdentity')}</h3>
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={form.control} name="first_name" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.firstName')}</FormLabel>
                      <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveFields('first_name'); }} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                  <FormField control={form.control} name="last_name" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('common.lastName')}</FormLabel>
                      <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveFields('last_name'); }} /></FormControl>
                      <FormMessage />
                    </FormItem>
                  )} />
                </div>
                <FormField control={form.control} name="email" render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('common.email')}</FormLabel>
                    <FormControl><Input type="email" {...field} onBlur={() => { field.onBlur(); void saveFields('email'); }} /></FormControl>
                    <FormMessage />
                  </FormItem>
                )} />
              </section>

              <section className="space-y-4">
                <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('volunteers.sheet.sectionRole')}</h3>
                <FormField control={form.control} name="role" render={({ field }) => (
                  <FormItem>
                    <Select value={field.value || NONE} onValueChange={(v) => { if (v) { field.onChange(v === NONE ? '' : v); void saveFields('role'); } }}>
                      <FormControl>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value={NONE}>{t('volunteers.list.noRole')}</SelectItem>
                        {ROLES.map((r) => <SelectItem key={r} value={r}>{t(`volunteers.role.${r}`)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    {definition?.roleField && (
                      <FormDescription>{t('volunteers.sheet.roleHint', { question: definition.roleField.label })}</FormDescription>
                    )}
                  </FormItem>
                )} />
              </section>

              {availability && (
                <section className="space-y-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('volunteers.sheet.sectionAvailability')}</h3>
                  <FormField control={form.control} name="answers" render={({ field }) => (
                    <FormItem>
                      <FormLabel className="font-normal text-muted-foreground">{availability.label}</FormLabel>
                      {renderAnswer(availability, field.value)}
                      <FormDescription>{t('volunteers.sheet.availabilityHint')}</FormDescription>
                    </FormItem>
                  )} />
                </section>
              )}

              {freeFields.length > 0 && (
                <section className="space-y-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('volunteers.sheet.sectionAnswers')}</h3>
                  <FormField control={form.control} name="answers" render={({ field }) => (
                    <div className="space-y-4">
                      {freeFields.map((f) => (
                        <FormItem key={f.id}>
                          {!(f.type === 'checkbox' && !f.options?.length) && <FormLabel>{f.label}</FormLabel>}
                          {f.type === 'checkbox' && !f.options?.length
                            ? <div className="text-sm"><span className="mb-2 block text-sm font-medium">{f.label}</span>{renderAnswer(f, field.value)}</div>
                            : renderAnswer(f, field.value)}
                        </FormItem>
                      ))}
                    </div>
                  )} />
                </section>
              )}

              {serverError && (
                <Alert variant="destructive">
                  <AlertDescription>{serverError}</AlertDescription>
                </Alert>
              )}
            </div>

            <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
              <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />
              </span>
              <Button type="button" variant="ghost" className="text-muted-foreground hover:text-destructive" onClick={onRemove} disabled={saving}>
                <Trash2 size={16} />{t('volunteers.remove.action')}
              </Button>
              <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
